// eslint-disable-next-line no-use-before-define
import React, { useState, useEffect, createContext, useRef } from 'react';
import { Flagship, FlagshipStatus } from '@flagship.io/js-sdk';
import { getModificationsFromCampaigns, logError, useNonInitialEffect } from './utils';
const initStat = {
    status: { isLoading: true, isSdkReady: false }
};
export const FlagshipContext = createContext({
    state: Object.assign({}, initStat)
});
export const FlagshipProvider = ({ children, fetchNow, envId, apiKey, decisionMode, decisionApiUrl, timeout, logLevel, statusChangedCallback, logManager, pollingInterval, visitorData, onInitStart, onInitDone, onBucketingSuccess, onBucketingFail, loadingComponent, onBucketingUpdated, onUpdate, enableClientCache, initialBucketing, initialCampaigns, initialModifications, initialFlagsData, fetchFlagsOnBucketingUpdated, activateDeduplicationTime, hitDeduplicationTime, visitorCacheImplementation, hitCacheImplementation, disableCache, language }) => {
    let modifications = new Map();
    if (initialFlagsData && initialFlagsData.forEach) {
        initialFlagsData.forEach((flag) => {
            modifications.set(flag.key, flag);
        });
    }
    else if (initialModifications && initialModifications.forEach) {
        initialModifications.forEach((modification) => {
            modifications.set(modification.key, modification);
        });
    }
    else if (initialCampaigns) {
        modifications = getModificationsFromCampaigns(initialCampaigns);
    }
    const [state, setState] = useState(Object.assign(Object.assign({}, initStat), { modifications }));
    const [lastModified, setLastModified] = useState();
    const stateRef = useRef();
    stateRef.current = state;
    useNonInitialEffect(() => {
        var _a;
        if (fetchFlagsOnBucketingUpdated) {
            (_a = state.visitor) === null || _a === void 0 ? void 0 : _a.fetchFlags();
        }
    }, [lastModified]);
    useNonInitialEffect(() => {
        createVisitor(true);
    }, [JSON.stringify(visitorData)]);
    useEffect(() => {
        initSdk();
    }, [envId, apiKey, decisionMode]);
    function initializeState(param) {
        const newStatus = {
            isSdkReady: param.isSdkReady,
            isLoading: param.isLoading,
            isVisitorDefined: !!param.fsVisitor,
            lastRefresh: new Date().toISOString()
        };
        setState((currentState) => {
            if (!currentState.status.firstInitSuccess) {
                newStatus.firstInitSuccess = new Date().toISOString();
            }
            return Object.assign(Object.assign({}, currentState), { visitor: param.fsVisitor, modifications: param.fsVisitor.modifications, config: Flagship.getConfig(), status: Object.assign(Object.assign({}, currentState.status), newStatus) });
        });
        return newStatus;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onVisitorReady = (fsVisitor, error) => {
        if (error) {
            logError(Flagship.getConfig(), error.message || error, 'onReady');
            return;
        }
        const newStatus = initializeState({
            fsVisitor,
            isSdkReady: true,
            isLoading: false
        });
        if (onUpdate) {
            onUpdate({
                fsModifications: fsVisitor.modifications,
                config: Flagship.getConfig(),
                status: newStatus
            });
        }
    };
    const createVisitor = (fetchFlags = false) => {
        if (!visitorData) {
            return;
        }
        const fsVisitor = Flagship.newVisitor({
            visitorId: visitorData.id,
            context: visitorData.context,
            isAuthenticated: visitorData.isAuthenticated,
            hasConsented: visitorData.hasConsented,
            initialCampaigns,
            initialModifications,
            initialFlagsData
        });
        fsVisitor === null || fsVisitor === void 0 ? void 0 : fsVisitor.on('ready', (error) => {
            onVisitorReady(fsVisitor, error);
        });
        if (!fetchNow && fsVisitor && !fetchFlags) {
            initializeState({
                fsVisitor,
                isSdkReady: true,
                isLoading: false
            });
        }
        if (fetchFlags && !fetchNow) {
            fsVisitor === null || fsVisitor === void 0 ? void 0 : fsVisitor.fetchFlags();
        }
    };
    const statusChanged = (status) => {
        if (statusChangedCallback) {
            statusChangedCallback(status);
        }
        switch (status) {
            case FlagshipStatus.STARTING:
                if (status === FlagshipStatus.STARTING && onInitStart) {
                    onInitStart();
                }
                break;
            case FlagshipStatus.READY_PANIC_ON:
            case FlagshipStatus.READY:
                if (onInitDone) {
                    onInitDone();
                }
                createVisitor();
                break;
            case FlagshipStatus.NOT_INITIALIZED:
                setState((prev) => (Object.assign(Object.assign({}, prev), { config: Flagship.getConfig() })));
                break;
        }
    };
    const onBucketingLastModified = (lastUpdate) => {
        if (onBucketingUpdated) {
            onBucketingUpdated(lastUpdate);
        }
        setLastModified(lastUpdate);
    };
    const initSdk = () => {
        Flagship.start(envId, apiKey, {
            decisionMode,
            fetchNow,
            timeout,
            logLevel,
            statusChangedCallback: statusChanged,
            logManager,
            pollingInterval,
            onBucketingFail,
            onBucketingSuccess,
            enableClientCache,
            decisionApiUrl,
            onBucketingUpdated: onBucketingLastModified,
            initialBucketing,
            activateDeduplicationTime,
            hitDeduplicationTime,
            visitorCacheImplementation,
            hitCacheImplementation,
            disableCache,
            language
        });
    };
    const handleDisplay = () => {
        const isFirstInit = !state.visitor;
        if (state.status.isLoading && loadingComponent && isFirstInit && fetchNow) {
            return React.createElement(React.Fragment, null, loadingComponent);
        }
        return React.createElement(React.Fragment, null, children);
    };
    return (React.createElement(FlagshipContext.Provider, { value: { state, setState } }, handleDisplay()));
};
FlagshipProvider.defaultProps = {
    activateDeduplicationTime: 2,
    hitDeduplicationTime: 2,
    fetchNow: true,
    language: 1
};
